/**
 * Live creator page after a scan (docs/SPEC.md §21 decision 75). The report carries a sync status for the signed-in
 * org (`GET /api/creators/:igPk/sync`, also inside the report): how much of its latest scan the server has written,
 * how many of its own just-uploaded reels are still being verified, and how many videos are still being saved. The app
 * polls it every SYNC_POLL_MS while anything is pending or the scan ended less than SYNC_RECENT_MS ago, and reloads the
 * report only when `version` changes. Pure, so the stop condition is unit-tested without a browser.
 */

export interface SyncStatus {
  /** The scan this status is about (the `?scan=` id the extension opened the page with, else this org's latest). */
  scanId: string | null;
  /** Reels the scan sent (batches + details batches) and reels the server has written so far. */
  reelsSent: number;
  reelsProcessed: number;
  /** When the scan's final batch arrived (null while it runs, or no scan). */
  scanFinishedAt: number | null;
  /** When the scan last sent a batch (null: no scan). */
  scanLastBatchAt: number | null;
  /** This org's own uploaded reels of the creator still waiting for corroboration (shown to this org, "verifying"). */
  verifying: number;
  /** Videos saved in our storage, and videos the archive is still saving (a fresh Instagram link, no copy yet). */
  videosSaved: number;
  videosPending: number;
  /** Changes whenever the report would: the client reloads the report only then. */
  version: string;
  /** Server time of this answer. */
  at: number;
}

export const SYNC_POLL_MS = 3_000;
/** A scan that ended this recently keeps the page polling (late videos, scores). */
export const SYNC_RECENT_MS = 3 * 60_000;
/** A scan with no batch for this long and no final batch is treated as over (the extension was closed). */
export const SYNC_STALL_MS = 3 * 60_000;
/**
 * Verifying reels and videos still saving count as "syncing" only this long after the scan's last batch: a reel that
 * never corroborates or a video the archive skipped must not keep the page saying "Syncing…" (decision 75 review).
 */
export const SYNC_SETTLE_MS = 10 * 60_000;
/** The page never polls longer than this in one go (a video that never saves can't keep it polling). */
export const SYNC_MAX_POLL_MS = 10 * 60_000;

/**
 * Something is still being written: the scan's reels not processed yet, or (within SYNC_SETTLE_MS of the scan's last
 * batch, judged on the server's clock) own reels being verified or videos being saved.
 */
export function syncPending(s: SyncStatus | null | undefined): boolean {
  if (!s) return false;
  if (s.reelsProcessed < s.reelsSent) return true;
  return scanSettling(s.scanLastBatchAt, s.scanFinishedAt, s.at) && (s.verifying > 0 || s.videosPending > 0);
}

/** Within SYNC_SETTLE_MS of the scan's last batch (or its end), on the server's clock `at`. */
export function scanSettling(scanLastBatchAt: number | null, scanFinishedAt: number | null, at: number): boolean {
  return scanLastBatchAt !== null && at - Math.max(scanLastBatchAt, scanFinishedAt ?? 0) < SYNC_SETTLE_MS;
}

/**
 * Whether the counts (verifying reels, videos saved and saving) can change anything the page shows (decision 94): only
 * while the scan's reels are still being written or the scan is settling. Otherwise syncPending is false whatever the
 * counts are, so syncLine shows nothing and the page doesn't poll; the signed-in report then skips the count read (it
 * read every reel of the creator) and answers zeros. scanRecent implies settling, so a polling page always gets them.
 */
export function syncCountsNeeded(scan: { reelsSent: number; reelsProcessed: number; scanLastBatchAt: number | null; scanFinishedAt: number | null } | null, at: number): boolean {
  if (!scan) return false;
  return scan.reelsProcessed < scan.reelsSent || scanSettling(scan.scanLastBatchAt, scan.scanFinishedAt, at);
}

/** The scan is running (a batch recently, no final) or ended less than SYNC_RECENT_MS ago. */
export function scanRecent(s: SyncStatus | null | undefined, now: number): boolean {
  if (!s || s.scanLastBatchAt === null) return false;
  if (s.scanFinishedAt !== null) return now - s.scanFinishedAt < SYNC_RECENT_MS;
  return now - s.scanLastBatchAt < SYNC_STALL_MS;
}

/**
 * Whether the page keeps polling: pending or a recent scan, and not past SYNC_MAX_POLL_MS since polling started
 * (`now` and `pollStartedAt` are the browser's clock; the scan's recency is judged on the server's, `s.at`).
 */
export function keepPolling(s: SyncStatus | null | undefined, now: number, pollStartedAt: number): boolean {
  if (!s || now - pollStartedAt >= SYNC_MAX_POLL_MS) return false;
  return syncPending(s) || scanRecent(s, s.at);
}

/**
 * The header's status text while syncing ("Syncing… 120/300 reels · 40 videos saving"), in the slot that otherwise says
 * "N reels awaiting verification"; null when nothing is pending.
 */
export function syncLine(s: SyncStatus | null | undefined): string | null {
  if (!syncPending(s)) return null;
  const parts: string[] = [];
  const reels = s!.reelsProcessed < s!.reelsSent;
  if (reels) parts.push(`${Math.min(s!.reelsProcessed, s!.reelsSent)}/${s!.reelsSent} reels`);
  else if (s!.verifying > 0) parts.push(`${s!.verifying} reel${s!.verifying === 1 ? '' : 's'} verifying`);
  if (s!.videosPending > 0) parts.push(`${s!.videosPending} video${s!.videosPending === 1 ? '' : 's'} saving`);
  return `Syncing… ${parts.join(' · ')}`;
}
